// Copyright 2025, University of Maryland, All Rights Reserved
import { axiosInstance } from './client';
import type {
  Document
} from './types';

export const documentsApi = {

  // Upload file - automatically chooses the right method based on file size
  async uploadFile(
    uploadUrl: string, 
    file: File, 
    org_id: string, 
    ws_id: string, 
    user_id: string, 
    document_type = 'exam', 
    username?: string, 
    folder?: string,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    if (uploadUrl === '/documents/upload-file') {
      const fileSizeMB = file.size / (1024 * 1024);
      // Lambda's 6MB invocation limit + API Gateway base64 encoding (~33% overhead) means
      // the effective ceiling for direct uploads is ~4MB raw binary.
      const LARGE_FILE_THRESHOLD_MB = 4;

      if (fileSizeMB > LARGE_FILE_THRESHOLD_MB) {
        console.log(`📤 File size: ${fileSizeMB.toFixed(2)}MB - Using presigned URL flow`);
        return await this.uploadLargeFile(file, org_id, ws_id, user_id, document_type, username, folder, onProgress);
      } else {
        console.log(`📤 File size: ${fileSizeMB.toFixed(2)}MB - Using direct upload`);
        return await this.uploadSmallFile(file, org_id, ws_id, user_id, document_type, username, folder, onProgress);
      }
    } else {
      // Original logic for other upload URLs
      console.log('Mock upload to:', uploadUrl);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  },

  // Direct upload for small files (<= 10MB)
  async uploadSmallFile(
    file: File,
    org_id: string,
    ws_id: string,
    user_id: string,
    document_type: string,
    username?: string,
    folder?: string,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', document_type);

    const params: Record<string, string> = {
      org_id,
      ws_id,
      user_id
    };

    if (username) {
      params.username = username;
    }

    if (folder) {
      params.folder = folder;
    }

    try {
      const response = await axiosInstance.post('/documents/upload-file', formData, {
        params,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        }
      });

      console.log('✅ Direct upload successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Direct upload failed:', error);
      // Re-throw with better error information
      if (error.response?.status === 422) {
        console.error('📄 Scanned image detected - no extractable text');
      }
      throw error;
    }
  },

  // Presigned URL upload for large files (> 10MB)
  async uploadLargeFile(
    file: File,
    org_id: string,
    ws_id: string,
    user_id: string,
    document_type: string,
    username?: string,
    folder?: string,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    try {
      // Step 1: Request presigned upload URL
      if (onProgress) onProgress(5);
      console.log('📋 Step 1: Requesting presigned upload URL...');
      
      const uploadRequest = await this.requestPresignedUpload(
        file.name,
        file.size,
        file.type || 'application/octet-stream',
        document_type,
        org_id,
        ws_id,
        user_id,
        username,
        folder
      );

      console.log('✅ Received presigned URL and document ID:', uploadRequest.document_id);

      // Step 2: Upload directly to S3
      if (onProgress) onProgress(10);
      console.log('📤 Step 2: Uploading to S3...');
      
      await this.uploadToS3(uploadRequest.presigned_url, file, file.type || 'application/octet-stream', (s3Progress) => {
        // Map S3 progress from 10% to 80%
        if (onProgress) {
          const mappedProgress = 10 + (s3Progress * 0.7);
          onProgress(Math.round(mappedProgress));
        }
      });

      console.log('✅ S3 upload successful');

      // Step 3: Confirm upload and create DynamoDB record
      if (onProgress) onProgress(85);
      console.log('📝 Step 3: Confirming upload and creating record...');
      
      const result = await this.confirmUpload(
        uploadRequest.document_id,
        uploadRequest.metadata
      );

      if (onProgress) onProgress(100);
      console.log('✅ Upload confirmed, document record created');

      return result;

    } catch (error: any) {
      console.error('❌ Large file upload failed:', error);
      throw error;
    }
  },

  // Request presigned upload URL
  async requestPresignedUpload(
    filename: string,
    file_size: number,
    content_type: string,
    document_type: string,
    org_id: string,
    ws_id: string,
    user_id: string,
    username?: string,
    folder?: string
  ): Promise<any> {
    const params: Record<string, string> = {
      filename,
      file_size: file_size.toString(),
      content_type,
      document_type,
      org_id,
      ws_id,
      user_id
    };

    if (username) {
      params.username = username;
    }

    if (folder) {
      params.folder = folder;
    }

    const response = await axiosInstance.post('/documents/request-presigned-upload', {}, {
      params
    });

    return response.data;
  },

  // Upload file directly to S3 using presigned URL
  async uploadToS3(
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(Math.round(progress));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('S3 upload failed'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('S3 upload aborted'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.setRequestHeader('x-amz-tagging', 'object_lifecycle=lifecycle_archive');
      xhr.send(file);
    });
  },

  // Confirm upload and create DynamoDB record
  async confirmUpload(
    document_id: string,
    metadata: any
  ): Promise<any> {
    const params: Record<string, string> = {
      s3_key: metadata.s3_key || '',
      filename: metadata.filename,
      file_size: metadata.file_size.toString(),
      content_type: metadata.content_type,
      document_type: metadata.document_type,
      org_id: metadata.org_id,
      ws_id: metadata.ws_id,
      user_id: metadata.user_id
    };

    if (metadata.username) {
      params.username = metadata.username;
    }

    if (metadata.folder) {
      params.folder = metadata.folder;
    }

    try {
      const response = await axiosInstance.post(`/documents/confirm-upload/${document_id}`, {}, {
        params
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Upload confirmation failed:', error);
      // Re-throw with better error information
      if (error.response?.status === 422) {
        console.error('📄 Scanned image detected - no extractable text');
      }
      throw error;
    }
  },

  // Get documents — loops through all pages using last_evaluated_key
  async getDocuments(org_id: string, ws_id: string): Promise<Document[]> {
    const all: Document[] = []
    let lastKey: string | undefined
    do {
      const response = await axiosInstance.get('/documents/', {
        params: {
          org_id,
          ws_id,
          ...(lastKey ? { last_evaluated_key: lastKey } : {})
        }
      })
      all.push(...response.data.documents)
      lastKey = response.data.last_evaluated_key ?? undefined
    } while (lastKey)
    return all
  },

  // Start document analysis
  async analyzeDocument(
    documentId: string,
    org_id: string,
    ws_id: string,
    user_id: string,
    username?: string,
    prompt_type?: string,
    prompt_name?: string,
    analysis_type?: string,
    template_type?: string,
    template_name?: string,
    llm_model?: string
  ): Promise<{ job_id: string; status: string; message: string }> {
    console.log(`[DEBUG] Starting analysis for document: ${documentId}`);
    try {
      const params: Record<string, string> = {
        org_id,
        ws_id,
        user_id
      };

      if (username) {
        params.username = username;
      }
      if (prompt_type) {
        params.prompt_type = prompt_type;
      }
      if (prompt_name) {
        params.prompt_name = prompt_name;
      }
      if (analysis_type) {
        params.analysis_type = analysis_type;
      }
      if (template_type) {
        params.template_type = template_type;
      }
      if (template_name) {
        params.template_name = template_name;
      }
      if (llm_model) {
        params.llm_model = llm_model;
      }

      const response = await axiosInstance.post(`/analysis/documents/${documentId}/analyze`, {}, {
        params
      });
      console.log(`[DEBUG] Analysis response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[DEBUG] Analysis failed:`, error);
      throw error;
    }
  },

  // Start multi-document analysis
  async multiAnalyzeDocuments(
    documentIds: string[],
    org_id: string,
    ws_id: string,
    user_id: string,
    username?: string,
    prompt_type?: string,
    prompt_name?: string,
    analysis_type?: string,
    template_type?: string,
    template_name?: string,
    llm_model?: string,
    analysis_summaries?: any[]
  ): Promise<{ job_id: string; status: string; message: string }> {
    const mode = analysis_summaries ? 'SUMMARY-BASED' : 'RAW DOCUMENT';
    console.log(`[DEBUG] Starting ${mode} multi-document analysis for ${documentIds.length} documents`);
    try {
      const params: Record<string, string> = {
        org_id,
        ws_id,
        user_id
      };

      if (username) {
        params.username = username;
      }
      if (prompt_type) {
        params.prompt_type = prompt_type;
      }
      if (prompt_name) {
        params.prompt_name = prompt_name;
      }
      if (analysis_type) {
        params.analysis_type = analysis_type;
      }
      if (template_type) {
        params.template_type = template_type;
      }
      if (template_name) {
        params.template_name = template_name;
      }
      if (llm_model) {
        params.llm_model = llm_model;
      }

      const requestBody: any = { document_ids: documentIds };
      
      // Add analysis summaries if provided (for summary-based analysis)
      if (analysis_summaries && analysis_summaries.length > 0) {
        requestBody.analysis_summaries = analysis_summaries;
        console.log(`[DEBUG] Including ${analysis_summaries.length} analysis summaries for synthesis`);
      }

      const response = await axiosInstance.post(`/analysis/documents/multi-analyze`, 
        requestBody,
        { params }
      );
      console.log(`[DEBUG] Multi-analysis response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[DEBUG] Multi-analysis failed:`, error);
      throw error;
    }
  },

  // Run CLO comparison across multiple completed LO analyses
  async runCLOComparison(
    analysisIds: string[],
    labels: string[],
    org_id: string,
    ws_id: string,
    user_id: string,
    llm_model?: string,
    is_prerequisite?: boolean
  ): Promise<{ job_id: string; status: string; comparison_id: string; message: string }> {
    const params: Record<string, string> = { org_id, ws_id, user_id };
    if (llm_model) params.llm_model = llm_model;

    const response = await axiosInstance.post(
      '/analysis/clo-comparison',
      { analysis_ids: analysisIds, labels, is_prerequisite: is_prerequisite ?? false },
      { params }
    );
    return response.data;
  },

  // Run a prerequisite check: one or more prerequisite courses vs a single requiring course
  async runPrerequisiteCheck(
    prerequisiteCourses: { label: string; analysis_id: string }[],
    requiringCourse: { label: string; analysis_id: string },
    org_id: string,
    ws_id: string,
    user_id: string,
    llm_model?: string
  ): Promise<{ job_id: string; status: string; check_id: string; message: string }> {
    const params: Record<string, string> = { org_id, ws_id, user_id };
    if (llm_model) params.llm_model = llm_model;

    const response = await axiosInstance.post(
      '/analysis/prerequisite-check',
      { prerequisite_courses: prerequisiteCourses, requiring_course: requiringCourse },
      { params }
    );
    return response.data;
  },

  // Run PLO mapping against user-supplied predefined PLOs (CSV)
  async runPLOMapping(
    comparisonIds: string[],
    labels: string[],
    ploFileBase64: string,
    org_id: string,
    ws_id: string,
    user_id: string,
    llm_model?: string,
    ploFilename?: string
  ): Promise<{ job_id: string; status: string; plo_id: string; message: string }> {
    const params: Record<string, string> = { org_id, ws_id, user_id };
    if (llm_model) params.llm_model = llm_model;

    const response = await axiosInstance.post(
      '/analysis/plo-mapping',
      { comparison_ids: comparisonIds, labels, plo_file: ploFileBase64, plo_filename: ploFilename },
      { params }
    );
    return response.data;
  },

  // Run PLO generation from multiple completed CLO comparison analyses
  async runPLOGeneration(
    comparisonIds: string[],
    labels: string[],
    org_id: string,
    ws_id: string,
    user_id: string,
    llm_model?: string
  ): Promise<{ job_id: string; status: string; plo_id: string; message: string }> {
    const params: Record<string, string> = { org_id, ws_id, user_id };
    if (llm_model) params.llm_model = llm_model;

    const response = await axiosInstance.post(
      '/analysis/plo-generation',
      { comparison_ids: comparisonIds, labels },
      { params }
    );
    return response.data;
  },

  // Delete document
  async deleteDocument(documentId: string, org_id: string, ws_id: string): Promise<void> {
    await axiosInstance.delete(`/documents/${documentId}`, {
      params: {
        org_id,
        ws_id
      }
    });
  },

  // Force analyze document (for DocumentsPage)
  async forceAnalyzeDocument(documentId: string, org_id: string, ws_id: string): Promise<any> {
    const response = await axiosInstance.post(`/documents/force-analyze/${documentId}`, {}, {
      params: {
        org_id,
        ws_id
      }
    });
    return response.data;
  },

  // Sync S3 to DynamoDB (for AnalysisPage)
  async syncS3ToDynamo(org_id: string, ws_id: string, user_id: string): Promise<any> {
    const response = await axiosInstance.post('/documents/sync-s3-to-dynamo', {}, {
      params: {
        org_id,
        ws_id,
        user_id
      }
    });
    return response.data;
      },

  // Download document
  async downloadDocument(documentId: string, org_id: string, ws_id: string): Promise<string> {
    const response = await axiosInstance.get(`/documents/${documentId}/download`, {
      params: {
        org_id,
        ws_id
      }
    });
    return response.data.download_url;
  },

  // Get list of all folders for a workspace
  async getFolders(org_id: string, ws_id: string): Promise<string[]> {
    const response = await axiosInstance.get('/documents/folders/', {
      params: {
        org_id,
        ws_id
      }
    });
    return response.data.folders || [];
  },

  // Rename a folder
  async renameFolder(
    oldFolderName: string, 
    newFolderName: string, 
    org_id: string, 
    ws_id: string
  ): Promise<{ message: string; documents_updated: number }> {
    const response = await axiosInstance.put(
      `/documents/folders/${encodeURIComponent(oldFolderName)}/rename`,
      {},
      {
        params: {
          new_folder_name: newFolderName,
          org_id,
          ws_id
        }
      }
    );
    return response.data;
  },

  // Delete a folder
  async deleteFolder(
    folderName: string,
    action: 'delete_documents' | 'orphan_documents',
    org_id: string,
    ws_id: string
  ): Promise<{ message: string; documents_deleted?: number; documents_orphaned?: number }> {
    const response = await axiosInstance.delete(
      `/documents/folders/${encodeURIComponent(folderName)}`,
      {
        params: {
          action,
          org_id,
          ws_id
        }
      }
    );
    return response.data;
  },

  // Rename a document
  async renameDocument(
    documentId: string,
    newFilename: string,
    org_id: string,
    ws_id: string,
    newDocumentType?: string,
    newFolder?: string
  ): Promise<{ message: string; document_id: string; old_filename: string; new_filename: string; old_document_type?: string; new_document_type?: string; old_folder?: string; new_folder?: string }> {
    const params: Record<string, string> = {
      new_filename: newFilename,
      org_id,
      ws_id
    };
    
    if (newDocumentType) {
      params.new_document_type = newDocumentType;
    }
    
    if (newFolder !== undefined) {
      params.new_folder = newFolder;
    }
    
    const response = await axiosInstance.put(
      `/documents/${documentId}/rename`,
      {},
      {
        params
      }
    );
    return response.data;
  }
};